<template>
    <div class="flex flex-col">
        <div class="mb-4 text-[20px] font-bold">All Students</div>
        <div
            class="mb-2 flex flex flex-col pt-4 lg:flex-row lg:justify-between"
        >
            <div class="w-full lg:order-last lg:w-1/4">
                <form-search
                    v-model="filters.search"
                    placeholder="Search for student by name"
                />
            </div>
            <div
                class="box-content flex h-10 items-center rounded border border-gray-350 text-center text-[15px]"
            >
                <label
                    :class="
                        'flex h-10 flex-grow items-center justify-center border-r border-gray-350 px-4 py-2 hover:cursor-pointer hover:bg-gray-250' +
                        ' ' +
                        (filters.status === 'active'
                            ? 'bg-gray-250 font-medium'
                            : 'bg-white text-gray-650')
                    "
                    ><input
                        class="hidden"
                        type="radio"
                        name="status[]"
                        v-model="filters.status"
                        value="active"
                    />Active&nbsp;({{
                        $page.props.group.active_students_count
                    }})</label
                >
                <label
                    :class="
                        'flex h-10 flex-grow items-center justify-center border-r border-gray-350 px-4 py-2 hover:cursor-pointer hover:bg-gray-250' +
                        ' ' +
                        (filters.status === 'pending'
                            ? 'bg-gray-250 font-medium'
                            : 'bg-white text-gray-650')
                    "
                    ><input
                        class="hidden"
                        type="radio"
                        name="status[]"
                        v-model="filters.status"
                        value="pending"
                    />Pending&nbsp;({{
                        $page.props.group.pending_students_count
                    }})</label
                >
                <label
                    :class="
                        'flex h-10 flex-grow items-center justify-center px-4 py-2 hover:cursor-pointer hover:bg-gray-250' +
                        ' ' +
                        (filters.status === 'expired'
                            ? 'bg-gray-250 font-medium'
                            : 'bg-white text-gray-650')
                    "
                    ><input
                        class="hidden"
                        type="radio"
                        name="status[]"
                        v-model="filters.status"
                        value="expired"
                    />Expired&nbsp;({{
                        $page.props.group.expired_students_count
                    }})</label
                >
            </div>
        </div>
        <easy-data-table
            v-if="filters.status === 'pending'"
            class="mobile-table group-dashboard-students table"
            v-model:server-options="serverOptions"
            :server-items-length="$page.props.students.total"
            :loading="loading"
            :headers="headers"
            :items="$page.props.students.data"
            :hide-footer="$page.props.students.total < 10"
            :rows-per-page="10"
            :rows-items="[10, 25, 50]"
            buttons-pagination
            :empty-message="getEmptyMessage()"
            must-sort
        >
            <template #item-full_name="student">
                <Link
                    class="group hover:cursor-pointer hover:text-blue-500"
                    :href="
                        route('group.student.show', {
                            user: student.id,
                        })
                    "
                >
                    <div class="flex items-center gap-2 whitespace-nowrap">
                        <span class="hidden lg:inline">
                            <UserAvatarRound :user="student" size="28" />
                        </span>
                        <span class="group-hover:underline">
                            <span
                                v-if="
                                    !student.subscription ||
                                    student.subscription.status !== 'incomplete'
                                "
                            >
                                {{ student.full_name }}</span
                            >
                            <span v-else> {{ student.email }}</span>
                        </span>
                    </div>
                </Link>
            </template>

            <template #item-provider_level.id="student">
                <ProviderLevelLabel
                    :providerLevelTitle="student.provider_level.title"
                />
            </template>

            <template #item-membership_length="student">
                {{ student.subscription.plan.length }} day
                <span class="hidden lg:inline">membership</span>
            </template>

            <template #item-active_classroom_name="student">
                <div v-if="!loading">
                    <GroupStudentClassroomSelectAndChange
                        :instructor-options="instructorOptions"
                        :classroom-options="classroomOptions"
                        :student="student"
                    />
                </div>
            </template>

            <template #item-created_at="student">
                <span class="text-gray-650">
                    {{
                        dayjs(student.created_at)
                            .utc(true)
                            .tz($page.props.auth.user.timezone)
                            .format($page.props.date_formats.short_nice)
                    }}
                </span>
            </template>

            <template #item-actions="student">
                <dropdown open-from="right" :px-width="260">
                    <template #button>
                        <more-dots-button />
                    </template>
                    <template #content>
                        <ul class="rounded border">
                            <li>
                                <Link
                                    as="div"
                                    :href="
                                        route('group.student.resend-invite', {
                                            user: student.id,
                                        })
                                    "
                                    class="px-4 py-2 hover:cursor-pointer hover:bg-gray-200"
                                    >Resend invite
                                </Link>
                            </li>
                            <li>
                                <GroupRevokeStudentInviteButton
                                    :student="student"
                                />
                            </li>
                        </ul>
                    </template>
                </dropdown>
            </template>
        </easy-data-table>
        <easy-data-table
            v-else
            class="mobile-table group-dashboard-students table"
            v-model:server-options="serverOptions"
            :server-items-length="$page.props.students.total"
            :loading="loading"
            :headers="headers"
            :items="$page.props.students.data"
            :hide-footer="$page.props.students.total < 10"
            :rows-per-page="10"
            :rows-items="[10, 25, 50]"
            buttons-pagination
            :empty-message="getEmptyMessage()"
            must-sort
        >
            <template #item-full_name="student">
                <Link
                    class="group hover:cursor-pointer hover:text-blue-500"
                    :href="
                        route('group.student.show', {
                            user: student.id,
                        })
                    "
                >
                    <div class="flex items-center gap-2 whitespace-nowrap">
                        <span class="hidden lg:inline">
                            <UserAvatarRound :user="student" size="28" />
                        </span>
                        <span class="group-hover:underline">
                            <span
                                v-if="
                                    !student.subscription ||
                                    student.subscription.status !== 'incomplete'
                                "
                            >
                                {{ student.full_name }}</span
                            >
                            <span v-else> {{ student.email }}</span>
                        </span>
                    </div>
                </Link>
            </template>

            <template #item-provider_level.id="student">
                <ProviderLevelLabel
                    :providerLevelTitle="student.provider_level.title"
                />
            </template>

            <template #item-membership_length="student">
                {{ student.subscription.plan.length }} day
                <span class="hidden lg:inline">membership</span>
            </template>

            <template #item-active_classroom_name="student">
                <div v-if="!loading">
                    <GroupStudentClassroomSelectAndChange
                        :instructor-options="instructorOptions"
                        :classroom-options="classroomOptions"
                        :student="student"
                    />
                </div>
            </template>

            <template #item-last_seen="student">
                <span class="text-gray-650">
                    <span v-if="!student.last_seen">--</span>
                    <span v-else>
                        {{
                            dayjs(student.last_seen)
                                .utc(true)
                                .tz($page.props.auth.user.timezone)
                                .format($page.props.date_formats.short_nice)
                        }}
                    </span>
                </span>
            </template>

            <template #item-actions="student">
                <dropdown open-from="right" :px-width="260">
                    <template #button>
                        <more-dots-button />
                    </template>
                    <template #content>
                        <ul class="rounded border">
                            <li
                                v-if="
                                    $page.props.auth.user
                                        .group_admin_permission_level !==
                                    'read-only'
                                "
                            >
                                <div class="px-4 py-2">
                                    <group-student-extend-membership-modal
                                        :student="student"
                                    />
                                </div>
                            </li>
                            <li
                                v-if="
                                    $page.props.auth.user
                                        .group_admin_permission_level !==
                                    'read-only'
                                "
                            >
                                <GroupRemoveStudentButton :student="student" />
                            </li>
                        </ul>
                    </template>
                </dropdown>
            </template>
        </easy-data-table>
    </div>
</template>
<script>
import Dropdown from "@/Components/Dropdown";
import MoreDotsButton from "@/Components/MoreDotsButton";
import ProviderLevelLabel from "@/Components/ProviderLevelLabel";
import GroupStudentClassroomSelectAndChange from "@/Components/GroupStudentClassroomSelectAndChange";
import FormSearch from "@/Components/FormSearch";
import UserAvatarRound from "@/Components/UserAvatarRound";
import GroupRevokeStudentInviteButton from "@/Components/GroupRevokeStudentInviteButton";
import GroupRemoveStudentButton from "@/Components/GroupRemoveStudentButton";
import GroupStudentExtendMembershipModal from "@/Components/GroupStudentExtendMembershipModal.vue";

export default {
    name: "group-dashboard-students-overview",
    components: {
        GroupStudentExtendMembershipModal,
        GroupRemoveStudentButton,
        GroupRevokeStudentInviteButton,
        GroupStudentClassroomSelectAndChange,
        ProviderLevelLabel,
        Dropdown,
        MoreDotsButton,
        FormSearch,
        UserAvatarRound,
    },

    data() {
        return {
            filters: this.$inertia.form({
                status: this.$page.props.currentFilters.status,
                search: this.$page.props.currentFilters.search,
            }),
            serverOptions: {
                page: this.$page.props.students.current_page,
                rowsPerPage: this.$page.props.students.per_page,
                sortBy: "full_name",
                sortType: "desc",
            },
            serverItemsLength: this.$page.props.students.total,
            loading: false,
            hideFooter: !(
                this.$page.props.students &&
                this.$page.props.students.total > 10
            ),
            classroomOptions: this.$page.props.classroomOptions,
            instructorOptions: this.$page.props.instructorOptions,
            headers: [],
        };
    },
    methods: {
        updateFilters() {
            let vm = this;

            this.$inertia.get(
                route("group.dashboard"),
                {
                    sortBy: this.serverOptions.sortBy,
                    sortType: this.serverOptions.sortType,
                    status: this.filters.status,
                    search: this.filters.search,
                    page: this.serverOptions.page,
                    perPage: this.serverOptions.rowsPerPage,
                },
                {
                    preserveState: true,
                    preserveScroll: true,
                    only: [
                        "students",
                        "currentFilters",
                        "serverOptions",
                        "group",
                    ],
                    onStart: function () {
                        vm.loading = true;
                    },
                    onFinish: function () {
                        vm.hideFooter =
                            vm.$page.props.students &&
                            vm.$page.props.students.total < 10;

                        vm.loading = false;
                    },
                }
            );
        },
        getEmptyMessage() {
            if (
                this.$page.props.group.pending_students_count == 0 &&
                this.$page.props.group.active_students_count == 0 &&
                this.$page.props.group.expired_students_count == 0
            ) {
                return "0 active students in your organization. Once you invite a student they’ll appear on this list.";
            } else {
                return "0 students found";
            }
        },
        updateHeaders() {
            if (this.filters.status === "pending") {
                this.headers = [
                    {
                        text: "Name",
                        value: "full_name",
                        sortable: true,
                    },
                    {
                        text: "Membership",
                        value: "membership_length",
                        sortable: true,
                    },
                    {
                        text: "Classroom",
                        value: "active_classroom_name",
                        sortable: true,
                    },
                    {
                        text: "Invite Sent",
                        value: "created_at",
                        sortable: true,
                    },
                    {
                        text: "",
                        value: "actions",
                        width: 30,
                    },
                ];
            } else {
                this.headers = [
                    {
                        text: "Name",
                        value: "full_name",
                        sortable: true,
                    },
                    {
                        text: "Level",
                        value: "provider_level.id",
                        sortable: true,
                    },

                    {
                        text: "Classroom",
                        value: "active_classroom_name",
                        sortable: true,
                    },
                    {
                        text: "Last Activity",
                        value: "last_seen",
                        sortable: true,
                    },
                    {
                        text: "",
                        value: "actions",
                        width: 30,
                    },
                ];
            }
        },
    },

    created() {
        this.updateHeaders();
    },
    watch: {
        filters: {
            handler: _.debounce(function (newValue, oldValue) {
                this.serverOptions.page = 1;
                this.updateHeaders();
                this.updateFilters();
            }, 250),
            deep: true,
        },
        serverOptions: {
            handler: _.debounce(function (newValue, oldValue) {
                this.updateFilters();
            }, 100),
            deep: true,
        },
    },
};
</script>
